import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import { AuthProvider, useAuthState } from '@/hooks/AuthContext';

import CallbackPage from './CallbackPage';
import Banner from './Pages/Banner';
import Welcome from './Pages/Welcome';
import Projects from './Pages/projects';
import NewProject from './Pages/NewProject';
import UserInspirationScreen from './Pages/UserInspirationScreen';
import UserMoodboardScreen from './Pages/UserMoodboardScreen';
import FloorPlanScreen from './Pages/FloorPlan';
import ApplianceSelector from './Pages/appliances';
import CabinetSelector from './Pages/cabinets';
import StartProjectScreen from './Pages/startProjectScreen';
import FileUploadScreen from './Pages/UploadFiles';
import AdditionalInfo from './Pages/AdditionalInfo';
import ProjectSummary from './Pages/ProjectSummaryPage';
import Soon from './Pages/Soon';
import AdminProjects from './Pages/AdminProjects'
import CollectionSummary from './Pages/collectionSummary';
import ConfigEditor from './Pages/startProjectConfigEditor';
import QuestionBuilder from './Pages/AdditionalInfoConfig';

import { initializeKindeAuth } from '@/services/tokenUtils';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { AssetsConfig } from '@/services/AssetsConfig';


const itsProd = false;

const AuthenticatedRoute = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuthState();

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to={process.env.PUBLIC_URL + '/commingsoon.html'} replace />;
    }

    return children;
};

const AdminRoute = ({ children }) => {
    const { permissions, isLoading } = useAuthState();

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!permissions?.includes('admin')) {
        return <Navigate to="/projects" replace />;
    }

    return children;
};


const App = () => {
    const kindeAuth = useKindeAuth();

    useEffect(() => {
        // Update favicon
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = AssetsConfig.getFaviconUrl();
        document.getElementsByTagName('head')[0].appendChild(link);
      }, []);

    useEffect(() => {
        initializeKindeAuth(kindeAuth);
    }, [kindeAuth]);


    return (
        <AuthProvider>
            <Router>
                <Routes>
                    <Route path="/banner" element={itsProd ? <Soon /> : <Banner />} />
                    <Route path="/" element={itsProd ? <Soon /> : <Welcome />} />
                    <Route path="/callback" element={itsProd ? <Soon /> : <CallbackPage />} />
                    <Route path="/welcome" element={itsProd ? <Soon /> : <Welcome />} />
                    <Route
                        path="/projects"
                        element={
                            <AuthenticatedRoute>
                                <Projects />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/new-project"
                        element={
                            <AuthenticatedRoute>
                                <StartProjectScreen />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId"
                        element={
                            <AuthenticatedRoute>
                                <NewProject />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/style"
                        element={
                            <AuthenticatedRoute>
                                <UserInspirationScreen />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/moodboard"
                        element={
                            <AuthenticatedRoute>
                                <UserMoodboardScreen />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/layout"
                        element={
                            <AuthenticatedRoute>
                                <FloorPlanScreen flowId="layout" />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/needs"
                        element={
                            <AuthenticatedRoute>
                                <AdditionalInfo flowId="needs" />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/upload"
                        element={
                            <AuthenticatedRoute>
                                <FileUploadScreen flowId="upload" />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/appliances"
                        element={
                            <AuthenticatedRoute>
                                <ApplianceSelector flowId="appliances" />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/cabinets"
                        element={
                            <AuthenticatedRoute>
                                <CabinetSelector flowId="cabinets" />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/summary"
                        element={
                            <AuthenticatedRoute>
                                <ProjectSummary />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/collection/:collectionId/summary"
                        element={
                            <AuthenticatedRoute>
                                <CollectionSummary />
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/admin/projects"
                        element={
                            <AuthenticatedRoute>
                                <AdminRoute>
                                    <AdminProjects />
                                </AdminRoute>
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/admin/config"
                        element={
                            <AuthenticatedRoute>
                                <AdminRoute>
                                    <ConfigEditor />
                                </AdminRoute>
                            </AuthenticatedRoute>
                        }
                    />
                    <Route
                        path="/admin/additional-info-config"
                        element={
                            <AuthenticatedRoute>
                                <AdminRoute>
                                    <QuestionBuilder />
                                </AdminRoute>
                            </AuthenticatedRoute>
                        }
                    />

                    <Route path="*" element={itsProd ? <Soon /> : <Navigate to="/welcome" replace />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
};

export default App;
